import { motion } from 'framer-motion';
import {
    ArrowRight,
    Award,
    Briefcase,
    CheckCircle,
    Globe,
    Heart,
    LogOut,
    Shield,
    Star,
    Target,
    TrendingUp,
    Users
} from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const About = () => {
  const { isAuthenticated, user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const stats = [
    { label: 'Active Jobs', value: '10,000+', icon: Briefcase, gradient: 'from-blue-500 to-cyan-500' },
    { label: 'Companies', value: '2,500+', icon: Globe, gradient: 'from-emerald-500 to-green-500' },
    { label: 'Job Seekers', value: '50,000+', icon: Users, gradient: 'from-purple-500 to-violet-500' },
    { label: 'Success Stories', value: '15,000+', icon: Award, gradient: 'from-amber-500 to-orange-500' }
  ];

  const values = [
    {
      icon: Target,
      title: 'Innovation First',
      description: 'We leverage cutting-edge technology to create the best job search experience for both candidates and employers.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Heart,
      title: 'People Centered',
      description: 'Every decision we make puts our users first. We believe great careers start with great connections.',
      gradient: 'from-rose-500 to-pink-500'
    },
    {
      icon: Shield,
      title: 'Trust & Transparency',
      description: 'We maintain the highest standards of integrity and transparency in all our interactions.',
      gradient: 'from-emerald-500 to-green-500'
    },
    {
      icon: CheckCircle,
      title: 'Quality Over Quantity',
      description: 'We focus on meaningful connections between the right candidates and the right opportunities.',
      gradient: 'from-purple-500 to-violet-500'
    }
  ];

  const team = [
    {
      name: 'Sarah Johnson',
      role: 'CEO & Founder',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b1fa?w=300&h=300&fit=crop&crop=face',
      bio: 'Former VP of Talent at top tech companies. Passionate about connecting great people with great opportunities.',
      linkedin: '#'
    },
    {
      name: 'Michael Chen',
      role: 'CTO',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
      bio: 'Tech veteran with 15+ years building scalable platforms. Led engineering teams at Fortune 500 companies.',
      linkedin: '#'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Head of Product',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
      bio: 'Product strategist focused on user experience. Former product lead at leading HR tech companies.',
      linkedin: '#'
    },
    {
      name: 'David Kim',
      role: 'Head of Partnerships',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
      bio: 'Business development expert with extensive network in recruitment and HR industry.',
      linkedin: '#'
    }
  ];

  const milestones = [
    { year: '2020', title: 'Company Founded', description: 'Started with a vision to revolutionize job search' },
    { year: '2021', title: '10K Users', description: 'Reached our first major milestone of active users' },
    { year: '2022', title: 'Series A Funding', description: 'Secured funding to accelerate growth and innovation' },
    { year: '2023', title: 'AI Integration', description: 'Launched advanced AI-powered job matching' },
    { year: '2024', title: '50K+ Community', description: 'Built a thriving community of professionals' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="nav-luxury sticky top-0 z-50">
        <div className="container-luxury">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">JobZentro</h1>
                  <p className="text-xs text-slate-500 font-medium">An AI HRMS Platform</p>
                </div>
              </Link>
              <nav className="hidden lg:flex space-x-8 ml-8">
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" className="nav-link">Dashboard</Link>
                    <Link to="/jobs" className="nav-link">Browse Jobs</Link>
                    <Link to="/applications" className="nav-link">Applications</Link>
                    <Link to="/profile" className="nav-link">Profile</Link>
                    <Link to="/about" className="nav-link active">About</Link>
                    <Link to="/contact" className="nav-link">Contact</Link>
                  </>
                ) : (
                  <>
                    <Link to="/" className="nav-link">Home</Link>
                    <Link to="/jobs" className="nav-link">Jobs</Link>
                    <Link to="/about" className="nav-link active">About</Link>
                    <Link to="/contact" className="nav-link">Contact</Link>
                  </>
                )}
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Link to="/profile" className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100 transition-colors">
                    <img
                      src={user?.profilePicture?.url || user?.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover shadow-soft"
                    />
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-heading font-semibold text-slate-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">Job Seeker</p>
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-xl hover:bg-slate-100"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden md:inline">Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-primary">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="section-luxury pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 hero-background opacity-10"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-green-400/20 to-blue-600/20 rounded-full blur-3xl animate-pulse-luxury"></div>
        
        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-white/20 shadow-soft mb-8">
              <Star className="w-4 h-4 text-amber-500 mr-2" />
              <span className="text-sm font-medium text-slate-700">Trusted by 50,000+ Professionals</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-display font-bold leading-tight mb-6">
              About
              <span className="block gradient-text text-shadow-lg">JobZentro</span>
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              We're on a mission to revolutionize how people find careers and how companies discover talent. 
              Through innovative technology and human-centered design, we're building the future of work.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/jobs" className="btn-primary text-lg px-10 py-5">
                Explore Opportunities
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/contact" className="btn-outline text-lg px-10 py-5">
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-luxury bg-white/50 backdrop-blur-sm">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6">
              Our
              <span className="block gradient-text">Impact</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Numbers that tell our story of connecting talent with opportunity.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card-luxury p-8 text-center group hover:scale-105 transition-all duration-300"
                >
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold font-heading mb-2 gradient-text-primary">{stat.value}</div>
                  <div className="text-slate-600 font-medium">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-luxury">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-6">
                <Target className="w-4 h-4 mr-2" />
                Our Mission
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6 text-slate-900">
                Transforming Careers,
                <span className="block gradient-text">One Connection at a Time</span>
              </h2>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                At JobZentro, we believe that finding the right job shouldn't be left to chance. 
                Our AI-powered platform uses advanced algorithms to match candidates with opportunities 
                that align with their skills, values, and career aspirations.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900 mb-1">Smart Matching Technology</h4>
                    <p className="text-slate-600">AI algorithms that understand both skills and cultural fit</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900 mb-1">Verified Companies</h4>
                    <p className="text-slate-600">Every employer is thoroughly vetted for legitimacy and quality</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900 mb-1">Career Growth Focus</h4>
                    <p className="text-slate-600">Tools and insights to help you advance your professional journey</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute inset-0 gradient-bg-luxury rounded-3xl blur-2xl opacity-20 animate-pulse-luxury"></div>
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop"
                alt="Team collaboration"
                className="relative card-luxury w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-luxury bg-gradient-to-br from-slate-50 to-white">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6">
              Our
              <span className="block gradient-text">Values</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              The principles that guide everything we do and shape our company culture.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="card-luxury p-8 group"
                >
                  <div className="flex items-start space-x-6">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${value.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow flex-shrink-0`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-heading font-semibold mb-4 text-slate-900">{value.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-luxury">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6">
              Our
              <span className="block gradient-text">Journey</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Key milestones that shaped our path to becoming a leading job platform.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 to-green-500 rounded-full"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="card-luxury p-6">
                      <div className="text-3xl font-bold gradient-text-primary mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">{milestone.title}</h3>
                      <p className="text-slate-600">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-green-500 rounded-full border-4 border-white shadow-lg z-10 flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-luxury bg-gradient-to-br from-slate-50 to-white">
        <div className="container-luxury">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6">
              Meet Our
              <span className="block gradient-text">Team</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              The passionate individuals behind JobZentro's mission to transform the future of work.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300"
              >
                <div className="relative mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-24 h-24 rounded-full mx-auto object-cover shadow-soft group-hover:shadow-lg transition-shadow"
                  />
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500/20 to-green-500/20 group-hover:opacity-0 transition-opacity"></div>
                </div>
                
                <h3 className="text-xl font-heading font-semibold text-slate-900 mb-1">{member.name}</h3>
                <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{member.bio}</p>
                
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors text-sm font-medium"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Connect
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-luxury gradient-bg-luxury text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-2xl animate-float"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-white rounded-full blur-3xl animate-pulse-luxury"></div>
        </div>
        
        <div className="container-luxury relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6">
              Ready to Transform Your Career?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Join thousands of professionals who have found their dream jobs through JobZentro. 
              Your next opportunity is just a click away.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="btn-glass text-lg px-10 py-5">
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/jobs" className="btn-outline border-white text-white hover:bg-white hover:text-slate-900 text-lg px-10 py-5">
                Browse Jobs
                <Briefcase className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="container-luxury">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 gradient-bg-luxury rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold">JobZentro</h3>
                  <p className="text-xs text-slate-400">An AI HRMS Platform</p>
                </div>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Connecting exceptional talent with outstanding opportunities through innovative technology and personalized service.
              </p>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">For Job Seekers</h4>
              <ul className="space-y-3">
                <li><Link to="/jobs" className="text-slate-400 hover:text-white transition-colors">Browse Jobs</Link></li>
                <li><Link to="/register" className="text-slate-400 hover:text-white transition-colors">Create Profile</Link></li>
                <li><Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">Dashboard</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Career Advice</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">For Employers</h4>
              <ul className="space-y-3">
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Post Jobs</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Employer Dashboard</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Search Talent</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Pricing</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-semibold text-lg mb-6">Company</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-slate-400 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="#" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-slate-400 text-center md:text-left">
              &copy; 2024 JobZentro. All rights reserved. Built with ❤️ for professionals worldwide.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <Heart className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <Globe className="w-5 h-5" />
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <TrendingUp className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About; 